/**
 * CompliancePage - TASK-252
 * Main compliance dashboard with tabs for Overview, Document Analysis, and Regulatory Feed
 * Enhanced with AML Sentinel integration
 */

import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import SecondaryNav from '../components/layout/SecondaryNav';
import {
  Shield,
  AlertTriangle,
  Clock,
  TrendingUp,
  FileSearch,
  Rss,
  Scale,
  BookOpen,
  ClipboardCheck,
  Loader2,
} from 'lucide-react';
import { DocumentAnalysisTab, RegulatoryFeedTab } from '../components/compliance-page';
import { listRegulations, listMonitoringRules } from '../services/amlService';
import { getComplianceSummary, type ComplianceSummary } from '../services/complianceActivityService';
import { deriveComplianceOverall } from '../utils/complianceStatus';
import { useAccount } from '../contexts/AccountContext';
import { useAlertStore } from '../stores/alertStore';
import { primaryTriageCount } from '../utils/triageCounts';

// TASK-374: Lazy-load review queue for CO users
const COReviewQueue = lazy(() => import('../components/compliance/review/COReviewQueue'));

type TabType = 'overview' | 'documents' | 'regulatory' | 'review';

const CompliancePage: React.FC = () => {
  const { isComplianceOfficer, isAdmin } = useAccount();
  const [activeTab, setActiveTab] = useState<TabType>(
    isComplianceOfficer ? 'review' : 'overview'
  );
  const [amlMetrics, setAmlMetrics] = useState({
    regulationsCount: 0,
    activeRulesCount: 0,
    loading: true,
  });
  // TASK-483: real account-level overview stats (replaces hardcoded 7 / 2 / 94% / 156).
  const [summary, setSummary] = useState<{ data: ComplianceSummary | null; loading: boolean }>({
    data: null,
    loading: true,
  });

  // Load AML metrics for overview
  useEffect(() => {
    const loadMetrics = async () => {
      const [regs, rules, summaryResult] = await Promise.allSettled([
        listRegulations({ limit: 1 }),
        listMonitoringRules(),
        getComplianceSummary(),
      ]);
      setAmlMetrics({
        regulationsCount: regs.status === 'fulfilled' ? regs.value.total : 0,
        activeRulesCount: rules.status === 'fulfilled' ? rules.value.total : 0,
        loading: false,
      });
      setSummary({
        data: summaryResult.status === 'fulfilled' ? summaryResult.value : null,
        loading: false,
      });
      if (summaryResult.status === 'rejected') {
        console.error('Failed to load compliance summary:', summaryResult.reason);
      }
    };
    loadMetrics();
  }, []);

  const tabs: { id: TabType; label: string; icon: typeof Shield }[] = [
    // TASK-374: Review Queue tab visible to CO and admin
    ...((isComplianceOfficer || isAdmin)
      ? [{ id: 'review' as TabType, label: 'Review Queue', icon: ClipboardCheck }]
      : []),
    { id: 'overview', label: 'Overview', icon: Shield },
    { id: 'documents', label: 'Document Analysis', icon: FileSearch },
    { id: 'regulatory', label: 'Regulatory Feed', icon: Rss },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SecondaryNav />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Compliance Dashboard</h2>
          <p className="text-gray-600 mt-1">
            Monitor regulatory compliance, analyze documents, and manage AML requirements
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'review' && (
          <Suspense fallback={<div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>}>
            <COReviewQueue />
          </Suspense>
        )}
        {activeTab === 'overview' && (
          <OverviewTab amlMetrics={amlMetrics} summary={summary} />
        )}
        {activeTab === 'documents' && <DocumentAnalysisTab />}
        {activeTab === 'regulatory' && <RegulatoryFeedTab />}
      </main>
    </div>
  );
};

// Overview Tab Component (merged existing content with AML metrics)
interface OverviewTabProps {
  amlMetrics: {
    regulationsCount: number;
    activeRulesCount: number;
    loading: boolean;
  };
  summary: { data: ComplianceSummary | null; loading: boolean };
}

const OverviewTab: React.FC<OverviewTabProps> = ({ amlMetrics, summary }) => {
  // TASK-483: render a real stat or a clean placeholder, never a fabricated number.
  const stat = (value: number | null | undefined, suffix = '') =>
    summary.loading ? '…' : value === null || value === undefined ? '—' : `${value}${suffix}`;

  // TASK-653: same records, same count as every other surface (shared store —
  // SecondaryNav on this page already fetched the list + mounted the WS).
  const alerts = useAlertStore((s) => s.alerts);
  const alertCounts = useAlertStore((s) => s.counts);
  const unresolvedAlertGroups = primaryTriageCount(alerts, alertCounts);
  // Zero-regulation honesty: with no regulations AND no monitoring rules, the
  // account's compliance monitoring is unconfigured — say so explicitly instead
  // of presenting alert counts as if regulatory checks produced them.
  const monitoringUnconfigured =
    !amlMetrics.loading && amlMetrics.regulationsCount === 0 && amlMetrics.activeRulesCount === 0;

  // TASK-555: derive the overall status from real summary signals — no hardcoded green
  // "Compliant". Score-thresholded via the shared helper (M1): a low score never reads green.
  const overall = deriveComplianceOverall(summary.data, { loading: summary.loading });
  const OverallIcon = overall.Icon;
  return (
    <div className="space-y-8">
      {/* TASK-653: unconfigured monitoring is a first-class state, not an
          unexplained wall of alerts. */}
      {monitoringUnconfigured && (
        <div
          data-testid="compliance-unconfigured"
          className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <p className="font-semibold">Compliance monitoring is not configured</p>
          <p className="mt-0.5">
            No regulations or monitoring rules are active for this account, so no regulatory
            checks are running. Alert groups shown below originate from portfolio, document,
            and task monitoring — not from regulatory rules. Configure regulations in the
            Regulatory Feed tab to enable regulatory checks.
          </p>
        </div>
      )}

      {/* Compliance Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Overall Status</p>
              <p className={`text-2xl font-bold mt-1 ${overall.text}`}>{overall.label}</p>
            </div>
            <OverallIcon className={`w-8 h-8 ${overall.icon}`} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Reviews</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stat(summary.data?.pending_reviews)}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        {/* TASK-653: THE primary triage number — unresolved alert groups from the
            shared alert store, identical to the bell, radar, and Alerts page.
            The old "Risk Alerts" figure was a raw count of ALL active alert
            signals (any category) from the compliance summary — a different
            number than every other surface showed. */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Unresolved Alert Groups</p>
              <p
                className="text-2xl font-bold text-gray-900 mt-1"
                data-testid="compliance-alert-groups"
              >
                {unresolvedAlertGroups}
              </p>
              <Link to="/alerts" className="text-xs text-indigo-600 hover:text-indigo-800">
                View in Alerts →
              </Link>
            </div>
            <AlertTriangle className="w-8 h-8 text-orange-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Compliance Score</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stat(summary.data?.compliance_score, '%')}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* AML Metrics Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-indigo-50 to-white p-6 rounded-lg shadow-sm border border-indigo-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-indigo-600 font-medium">Active Regulations</p>
              <p className="text-3xl font-bold text-indigo-900 mt-1">
                {amlMetrics.loading ? '...' : amlMetrics.regulationsCount}
              </p>
              <p className="text-xs text-indigo-500 mt-1">From regulatory feeds</p>
            </div>
            <BookOpen className="w-10 h-10 text-indigo-400" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-lg shadow-sm border border-purple-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">Monitoring Rules</p>
              <p className="text-3xl font-bold text-purple-900 mt-1">
                {amlMetrics.loading ? '...' : amlMetrics.activeRulesCount}
              </p>
              <p className="text-xs text-purple-500 mt-1">AI-extracted from regulations</p>
            </div>
            <Scale className="w-10 h-10 text-purple-400" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-50 to-white p-6 rounded-lg shadow-sm border border-emerald-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-600 font-medium">Documents Verified</p>
              <p className="text-3xl font-bold text-emerald-900 mt-1">{stat(summary.data?.documents_verified)}</p>
              <p className="text-xs text-emerald-500 mt-1">Account total</p>
            </div>
            <FileSearch className="w-10 h-10 text-emerald-400" />
          </div>
        </div>
      </div>

      {/* Regulatory Requirements — TASK-483: the per-regulation status list was hardcoded
          (fabricated "Last updated"/"Due" values). The real regulatory state lives in the
          Regulatory Feed tab; surface the live active-regulation count and point there. */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Regulatory Requirements</h3>
        <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg">
          <div className="flex items-center">
            <BookOpen className="w-5 h-5 text-indigo-500 mr-3" />
            <div>
              <p className="font-medium text-gray-900">
                {amlMetrics.loading ? '…' : amlMetrics.regulationsCount} active regulations monitored
              </p>
              <p className="text-sm text-gray-600">
                Live regulatory state and updates are tracked in the Regulatory Feed tab.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Items */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Action Items</h3>
          <button className="text-sm text-indigo-600 hover:text-indigo-800">View All</button>
        </div>
        {/* TASK-555: action items derived from real summary signals — no fabricated ESG/training/
            FINMA items with invented due dates. Honest empty state when nothing is open. */}
        <div className="space-y-3">
          {summary.loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (() => {
            const d = summary.data;
            const items: { Icon: typeof Clock; cls: string; title: string; sub: string }[] = [];
            if ((d?.pending_reviews ?? 0) > 0)
              items.push({ Icon: Clock, cls: 'text-amber-500', title: `${d!.pending_reviews} compliance review(s) pending`, sub: 'Action them in the Review Queue.' });
            if ((d?.risk_alerts ?? 0) > 0)
              items.push({ Icon: AlertTriangle, cls: 'text-orange-500', title: `${d!.risk_alerts} risk alert(s) open`, sub: 'Investigate flagged clients in AML screening.' });
            if (!items.length)
              return <p className="text-sm text-gray-500">No open compliance action items.</p>;
            return items.map((it, i) => {
              const ItemIcon = it.Icon;
              return (
                <div key={i} className="flex items-start p-3 bg-gray-50 rounded-lg">
                  <ItemIcon className={`w-5 h-5 ${it.cls} mr-3 mt-0.5`} />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{it.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{it.sub}</p>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>
    </div>
  );
};

export default CompliancePage;
